// Shown when an emailed code confirmed a new account but the password chosen at sign-up couldn't be saved again
// afterwards (WEB_PLAN.md §4.2: the server drops passwords set before confirmation). The account is confirmed and
// signed in; the finishing gate is still held so this stays on screen until the person continues.
//
// Leaving the notice any other way (browser Back, a link) releases the gate too: otherwise the confirmed session
// would never count and every later sign-in in this tab would look ignored. The account is the person's either way
// (iOS keeps it signed in with the same notice).

import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PASSWORD_NOT_SAVED_TEXT } from '@/auth/errors';
import { completeSignIn } from '@/auth/flows';
import { PrimaryButton } from './ui';

/**
 * A release scheduled by an unmount. React StrictMode unmounts and remounts once in development; the remount cancels
 * the release so the notice isn't skipped. Module-level because the remount is a new effect run of the same component.
 */
let scheduledRelease: number | undefined;

export function PasswordNotSavedNotice() {
  // Release the gate once: AuthProvider then resolves the account and this page goes away.
  const [continuing, setContinuing] = useState(false);
  const onContinue = () => {
    if (continuing) return;
    setContinuing(true);
    completeSignIn();
  };

  useEffect(() => {
    window.clearTimeout(scheduledRelease);
    scheduledRelease = undefined;
    return () => {
      // completeSignIn is idempotent: after Continue this does nothing.
      scheduledRelease = window.setTimeout(() => {
        scheduledRelease = undefined;
        completeSignIn();
      }, 0);
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div role="alert" className="flex items-start gap-3 rounded-xl bg-[var(--gt-amber)]/15 p-4 text-sm text-slate-800 dark:text-slate-100">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gt-amber)]" aria-hidden />
        <p>{PASSWORD_NOT_SAVED_TEXT}</p>
      </div>
      <PrimaryButton onClick={onContinue} busy={continuing} busyLabel="Signing in…">
        Continue
      </PrimaryButton>
    </div>
  );
}
